import { FC } from 'react';
import {
    Box, Chip, CircularProgress, Divider, Table, TableBody, TableCell,
    TableHead, TableRow, Typography,
} from '@mui/material';
import { useEppiDetail } from '../../../../hooks/useUser';
import { fmtDate, fmtDateOnly } from '../../userShowUtils';

/** ④-4. 스티커북 — 부화 상태 + 대표 에삐 + 착용 슬롯 + 보유 코스튬 목록 + 방 배치 슬롯 */
const EppiTab: FC<{ userId: number }> = ({ userId }) => {
    const { data, isLoading, error } = useEppiDetail(userId);

    if (isLoading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress size={24} /></Box>;
    if (error || !data) return <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>스티커북 정보를 불러오지 못했습니다.</Typography>;

    return (
        <Box>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Chip size="small" label={data.hatched ? '부화' : '미부화'} color={data.hatched ? 'success' : 'default'} />
                <Typography variant="body2" color="text.secondary">
                    대표 에삐: {data.representativeCostumeName ?? '-'}
                </Typography>
            </Box>

            <Typography variant="subtitle1" fontWeight={600} gutterBottom>착용 슬롯</Typography>
            {data.equippedSlots.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 2 }}>착용 중인 코스튬이 없습니다.</Typography>
            ) : (
                <Table size="small" sx={{ mb: 3 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>슬롯</TableCell>
                            <TableCell>코스튬</TableCell>
                            <TableCell>착용 일시</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.equippedSlots.map((s, idx) => (
                            <TableRow key={`${s.slotType}-${idx}`}>
                                <TableCell>{s.slotType}</TableCell>
                                <TableCell>{s.costumeName}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(s.equippedAt)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}

            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>보유 코스튬 ({data.ownedCostumes.length.toLocaleString()})</Typography>
            {data.ownedCostumes.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 2 }}>보유한 코스튬이 없습니다.</Typography>
            ) : (
                <Table size="small" sx={{ mb: 3 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>코스튬</TableCell>
                            <TableCell>카테고리</TableCell>
                            <TableCell>렌더 종류</TableCell>
                            <TableCell>미션보상</TableCell>
                            <TableCell align="right">구매 깃털</TableCell>
                            <TableCell>획득 일시</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.ownedCostumes.map((c) => (
                            <TableRow key={c.costumeId}>
                                <TableCell>{c.name}</TableCell>
                                <TableCell>{c.categoryName ?? '-'}</TableCell>
                                <TableCell>{c.renderKind ?? '-'}</TableCell>
                                <TableCell>{c.isMissionReward ? <Chip size="small" label="미션" color="info" /> : '-'}</TableCell>
                                <TableCell align="right">{c.purchaseXpCost.toLocaleString()}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDateOnly(c.acquiredAt)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}

            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>방 배치 슬롯 ({data.roomItems.length.toLocaleString()})</Typography>
            {data.roomItems.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 2 }}>방을 꾸미지 않았습니다.</Typography>
            ) : (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>슬롯</TableCell>
                            <TableCell>코스튬</TableCell>
                            <TableCell>텍스트</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.roomItems.map((r) => (
                            <TableRow key={r.slotIndex}>
                                <TableCell>{r.slotIndex}</TableCell>
                                <TableCell>{r.costumeName}</TableCell>
                                <TableCell>{r.text ?? '-'}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </Box>
    );
};

export default EppiTab;
